"use client";

import React from "react";
import { Ruler, Layers, Truck, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "01",
  title: "Foundation Prep",
  description: "Rigorous site grading and soil compaction to prevent future settling.",
  spec: "95% Soil Compaction",
  icon: Ruler
}, {
  id: "02",
  title: "Reinforcement",
  description: "Installation of heavy-duty steel rebar or wire mesh grids for structural integrity.",
  spec: "#4 Rebar @ 16\" OC",
  icon: Layers
}, {
  id: "03",
  title: "Pouring",
  description: "High-strength concrete mix delivery with precise slump control and placement.",
  spec: "4000+ PSI Mix",
  icon: Truck
}, {
  id: "04",
  title: "Finishing",
  description: "Expert troweling, texturing, and application of curing compounds for longevity.",
  spec: "Penetrating Sealer",
  icon: ShieldCheck
}];
export default function TheDurabilityStandard() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-durability-standard" ref={ref} className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
      backgroundSize: '40px 40px'
    }} />
      
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className={`mb-16 max-w-3xl transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-primary/50"></span>
            <span className="text-primary font-mono text-sm tracking-widest uppercase">Technical Process</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white">
            The Durability Standard
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed">
            Our concrete isn't just poured; it's engineered. We follow a strict 4-step protocol designed to withstand Northern Indiana's freeze-thaw cycles and heavy loads.
          </p>
        </div>

        {}
        <div className="relative grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-0">
          
          {}
          <div className="hidden lg:block absolute top-12 left-0 w-full h-px bg-border/30 z-0" />
          
          {}
          <div className="lg:hidden absolute top-0 left-8 w-px h-full bg-border/30 z-0" />

          {steps.map((step, i) => <div key={step.id} data-index={i} className={`relative group pt-0 lg:pt-12 pl-20 lg:pl-6 pr-6 transition-all duration-700 delay-[${i * 150}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              {}
              <div className="absolute left-6 lg:left-6 top-0 lg:top-12 -translate-x-1/2 -translate-y-0 lg:-translate-y-1/2 z-10">
                <div className="w-4 h-4 rounded-full bg-background border-2 border-primary ring-4 ring-background group-hover:scale-125 transition-transform duration-300" />
              </div>

              {}
              <div className="absolute -top-4 right-4 lg:right-auto lg:left-4 text-9xl font-black text-white/5 select-none pointer-events-none z-0 leading-none">
                {step.id}
              </div>

              {}
              <div className="relative z-10 h-full flex flex-col">
                {}
                <div className="mb-4 flex items-center gap-3">
                  <div className="p-2 rounded bg-primary/10 border border-primary/20 text-primary">
                    <step.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{step.title}</h3>
                </div>

                {}
                <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-grow">
                  {step.description}
                </p>

                {}
                <div className="mt-auto">
                  <Separator className="mb-3 bg-white/10" />
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-1">
                      Tech Spec
                    </span>
                    <span className="text-primary font-mono font-bold text-sm tracking-tight bg-primary/5 px-2 py-1 rounded border border-primary/10 inline-block w-fit">
                      {step.spec}
                    </span>
                  </div>
                </div>

                {}
                <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-white/20" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-white/20" />
              </div>
            </div>)}
        </div>

        {}
        <div className={`mt-20 flex flex-col md:flex-row items-center justify-between gap-6 border-t border-white/10 pt-8 transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex flex-col gap-1">
            <span className="text-white font-semibold">Ready to build on solid ground?</span>
            <span className="text-muted-foreground text-sm">Get a free estimate for your residential or commercial project.</span>
          </div>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
            <Link to="tel:317-709-9540">
              call to schedule a quote
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}